import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { MessageBarActions, MessageBarBody, MessageBarTitle, Spinner } from '@fluentui/react-components';
import { ChevronDown20Regular, ChevronRight20Regular, DocumentDismiss20Regular } from '@fluentui/react-icons';

import * as ComponentFunctions from './ProviderSettlementDetails.Functions';
import {
    CurrencyTotal,
    groupDetailsByProtocol,
    NO_PROTOCOL_KEY,
    settlementFailureMessage,
    settlementTotals,
} from './ProviderSettlementDetails.Logic';
import { useSettlementExecutionPolling } from './useSettlementExecutionPolling';
import { GuideTable, GuideTh, guideCellClassName, guideRowClassName } from '../../../theme/components/GuideTable';
import { GuideMessageBar } from '../../../theme/components/GuideMessageBar';
import { GuideBannerAction } from '../../../theme/components/GuideButtons';
import { GuideSkeleton } from '../../../theme/components/GuideSkeleton';
import { GuideEmptyState } from '../../../theme/components/GuideEmptyState';
import { formatCurrencyAmountWithIssue } from '../../../commons/currency/Currency.Functions';
import { SettlementStatusEnum } from '../../../models/Enums/SettlementStatusEnum';

import './ProviderSettlementDetails.Styles.css';

const fmtDate = (value: Date | undefined) => (value ? moment(value).format('DD/MM/YYYY') : '—');

/**
 * Detalle de una liquidación de proveedor (spec 010 US4): líneas agrupadas por protocolo ("Sin protocolo" al
 * final), con subtotales y total por moneda; "Calculando…" con actualización cada 5 s mientras se genera y el
 * `comments` de la liquidación si falló.
 */
export const ProviderSettlementDetailsComponent = (props: ComponentFunctions.Props) => {
    const { t } = useTranslation();
    const tr = (path: string, options?: Record<string, unknown>) => t(`providerSettlement.${path}`, options) as string;
    const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());

    const settlementId = props.SettlementId;
    const status = props.Settlement?.status;

    useEffect(() => {
        if (!settlementId) return;
        props.GetSettlementById(settlementId);
        props.GetAllSettlementDetails(settlementId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settlementId]);

    const calculating = useSettlementExecutionPolling(
        settlementId,
        status,
        props.RefreshSettlementById,
        props.GetAllSettlementDetails
    );

    // Un refresco de la liquidación (cabecera, cambio de estado) la reemplaza sin detalles: se vuelven a pedir.
    useEffect(() => {
        if (!settlementId || !props.Settlement || calculating) return;
        if (props.details === undefined && !props.isGettingDetails && !props.failOnGettingDetails) {
            props.GetAllSettlementDetails(settlementId);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settlementId, !!props.Settlement, calculating, props.details === undefined]);

    const groups = useMemo(
        () => groupDetailsByProtocol(props.details, props.ProtocolTitleOf, tr('noProtocol')),
    // eslint-disable-next-line react-hooks/exhaustive-deps
        [props.details, props.ProtocolTitleOf, t]
    );
    const totals = useMemo(() => settlementTotals(groups), [groups]);
    const failed = status === SettlementStatusEnum.FAIL;
    const failureMessage = settlementFailureMessage(props.Settlement);

    const toggle = (key: string) =>
        setCollapsed((current) => {
            const next = new Set(current);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });

    const fmt = (amount: number, currency: string) => formatCurrencyAmountWithIssue(amount, currency || props.Settlement?.currencyCode);

    const renderTotals = (items: CurrencyTotal[], className: string) => (
        <span className={className}>
            {items.map((total) => (
                <span key={total.currency || 'none'} className="psd-amount ichtys-num">
                    {fmt(total.amount, total.currency)}
                </span>
            ))}
        </span>
    );

    const renderBody = () => {
        if (calculating) {
            return (
                <GuideMessageBar intent="info" layout="multiline" data-testid="psd-calculating">
                    <MessageBarBody>
                        <MessageBarTitle>
                            <Spinner size="extra-tiny" className="psd-inline-spinner" aria-hidden="true" />
                            {tr('calculating')}
                        </MessageBarTitle>
                        {tr('calculatingHelp')}
                    </MessageBarBody>
                </GuideMessageBar>
            );
        }
        if (failed) {
            return (
                <GuideMessageBar intent="error" layout="multiline" data-testid="psd-failed">
                    <MessageBarBody>
                        <MessageBarTitle>{tr('failed')}</MessageBarTitle>
                        {/* Texto del servidor: siempre como texto, nunca como HTML. */}
                        <span data-testid="psd-failed-message">{failureMessage ?? tr('failedNoMessage')}</span>
                    </MessageBarBody>
                </GuideMessageBar>
            );
        }
        if (props.failOnGettingDetails && props.details === undefined) {
            return (
                <GuideMessageBar intent="warning" layout="multiline" data-testid="psd-load-error">
                    <MessageBarBody>
                        <MessageBarTitle>{tr('loadError')}</MessageBarTitle>
                    </MessageBarBody>
                    <MessageBarActions>
                        <GuideBannerAction onClick={() => props.GetAllSettlementDetails(settlementId)} data-testid="psd-load-retry">
                            {tr('retry')}
                        </GuideBannerAction>
                    </MessageBarActions>
                </GuideMessageBar>
            );
        }
        if (props.details === undefined) {
            return <GuideSkeleton rows={4} data-testid="psd-loading" />;
        }
        if (groups.length === 0) {
            return <GuideEmptyState title={tr('empty')} text={tr('emptyHelp')} data-testid="psd-empty" />;
        }
        return (
            <>
                {groups.map((group) => {
                    const isCollapsed = collapsed.has(group.key);
                    const bodyId = `psd-group-body-${group.key}`;
                    const isNone = group.key === NO_PROTOCOL_KEY;
                    return (
                        <div key={group.key} className={`psd-group${isNone ? ' psd-group--none' : ''}`} data-testid={`psd-group-${group.key}`}>
                            <button
                                type="button"
                                className="psd-group__header"
                                aria-expanded={!isCollapsed}
                                aria-controls={bodyId}
                                aria-label={tr('group.toggle', { name: group.label })}
                                onClick={() => toggle(group.key)}
                                data-testid={`psd-group-toggle-${group.key}`}
                            >
                                <span className="psd-group__chevron" aria-hidden="true">
                                    {isCollapsed ? <ChevronRight20Regular /> : <ChevronDown20Regular />}
                                </span>
                                {isNone && (
                                    <span className="psd-group__none-icon" aria-hidden="true">
                                        <DocumentDismiss20Regular />
                                    </span>
                                )}
                                <span className="psd-group__label" title={group.label}>{group.label}</span>
                                <span className="psd-group__count">{tr('group.lines', { count: group.lines.length })}</span>
                                {renderTotals(group.totals, 'psd-group__totals')}
                            </button>
                            {!isCollapsed && (
                                <div id={bodyId} className="psd-group__body">
                                    <GuideTable ariaLabel={tr('group.tableLabel', { name: group.label })}>
                                        <thead>
                                            <tr>
                                                <GuideTh>{tr('columns.patient')}</GuideTh>
                                                <GuideTh>{tr('columns.date')}</GuideTh>
                                                <GuideTh>{tr('columns.service')}</GuideTh>
                                                <GuideTh align="end">{tr('columns.quantity')}</GuideTh>
                                                <GuideTh align="end">{tr('columns.unitAmount')}</GuideTh>
                                                <GuideTh align="end">{tr('columns.amount')}</GuideTh>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {group.lines.map((line) => (
                                                <tr key={line.id} className={guideRowClassName()} data-testid={`psd-line-${line.id}`}>
                                                    <td>{line.patient || '—'}</td>
                                                    <td className={guideCellClassName('num')}>{fmtDate(line.date)}</td>
                                                    <td className="psd-service">{line.service || '—'}</td>
                                                    <td className={guideCellClassName('num', 'end')}>{line.quantity}</td>
                                                    <td className={guideCellClassName('num', 'end')}>{fmt(line.unitAmount, line.currency)}</td>
                                                    <td className={guideCellClassName('amount', 'end')}>{fmt(line.amount, line.currency)}</td>
                                                </tr>
                                            ))}
                                            {group.totals.map((total) => (
                                                <tr key={`subtotal-${total.currency}`} className={guideRowClassName({ total: true })} data-testid={`psd-subtotal-${group.key}-${total.currency}`}>
                                                    <td colSpan={5}>{tr('group.subtotal', { currency: total.currency })}</td>
                                                    <td className={guideCellClassName('amount', 'end')}>{fmt(total.amount, total.currency)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </GuideTable>
                                </div>
                            )}
                        </div>
                    );
                })}
                <div className="psd-footer" data-testid="psd-total">
                    <span className="psd-footer__label">{tr('total')}</span>
                    <ul className="psd-footer__totals" aria-label={tr('total')}>
                        {totals.map((total) => (
                            <li key={total.currency || 'none'} data-testid={`psd-total-${total.currency}`}>
                                <span className="psd-footer__currency">{total.currency}</span>
                                <span className="psd-amount ichtys-num">{fmt(total.amount, total.currency)}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </>
        );
    };

    return (
        <section className="psd-root" aria-labelledby="psd-title" aria-busy={calculating || (!failed && props.details === undefined) || undefined} data-testid="provider-settlement-details">
            <header className="psd-header">
                <h3 id="psd-title" className="psd-title">{tr('title')}</h3>
            </header>
            <div aria-live="polite">{renderBody()}</div>
        </section>
    );
};

export default connect(
    ComponentFunctions.mapStateToProps,
    ComponentFunctions.mapDispatchToProps
)(ProviderSettlementDetailsComponent as any);
